// fatal-notice.ts — what the player is told when Dice cannot run the launched mode (SC-61728, SC-61814). boot.ts renders
// it in the full-screen block panel, above the SDK boot loader.
//
// A REAL session can end fatal three ways (SDK 0.8.0): the ticket exchange or the session re-read was refused at boot,
// or the platform refused a call MID-PLAY because the session is over (SC-62075/SC-62084: session_invalid /
// session_expired — a reload cannot revive it; SC-66897: game_not_launched / game_disabled — the session cannot play
// Dice), or a REAL resume could not re-read the balance (balance_read_failed: the
// session is kept — SC-61762 fails the restore closed rather than show a balance the player may not have). Dice adds two
// of its own: launch_required (the URL carries no launch, see launch-decision.ts) and launch_failed (the SDK boot itself
// threw). Reload is offered only when a reload can resume this launch.

export interface FatalNotice {
  readonly title: string;
  readonly lines: readonly string[];
  /** Offer a Reload button: only when the session is kept AND the URL is a resumable REAL launch (#mode=real). */
  readonly reload: boolean;
}

export interface FatalContext {
  /** The launch was (or resumes) REAL money. */
  readonly realLaunch: boolean;
  /** A reload would resume this launch: the URL is a REAL resume (#mode=real). */
  readonly resumable: boolean;
}

/** The session's currency is one the game cannot play (SC-61816, currency.ts), so it does not run. The casino session
 *  itself is fine: relaunching or reloading would land here again, so the notice sends the player to change the
 *  currency instead. `code` is the currency when it is safe to print (currency.ts displayableCode). */
export function currencyNotice(code: string | null): FatalNotice {
  return {
    title: "Dice is not available in this currency",
    lines: [
      code ? `This game cannot be played in ${code} yet.` : "This game cannot be played in this session's currency yet.",
      "Please go back to the casino and choose another currency.",
    ],
    reload: false,
  };
}

export function fatalNotice(ctx: FatalContext, code: string): FatalNotice {
  if (code === "launch_required") {
    return { title: "Launch Dice from the casino", lines: ["Open Dice from the casino to play."], reload: false };
  }
  if (!ctx.realLaunch) return { title: "Dice unavailable", lines: ["The game hit a fatal error."], reload: false };
  switch (code) {
    case "balance_read_failed":
      return {
        title: "We couldn't load your balance",
        lines: ["Your session is still open. Reload the game to try again."],
        reload: ctx.resumable,
      };
    case "session_invalid":
    case "session_expired":
      return { title: "This session is no longer active", lines: ["Please relaunch Dice from the casino to keep playing."], reload: false };
    // SC-66897 (SDK 0.30.0): the gateway refused a REAL call for a game this session cannot play — it was launched for
    // another game, or the casino no longer offers Dice. Nothing more is sent (the SDK ends the session), and a reload
    // would land here again: only a launch from the casino can help.
    case "game_not_launched":
      return { title: "This session was opened for another game", lines: ["Please launch Dice again from the casino."], reload: false };
    case "game_disabled":
      return { title: "Dice is not available right now", lines: ["Please go back to the casino and launch the game again."], reload: false };
    default:
      return { title: "Dice couldn't start", lines: ["Please launch it again from the casino."], reload: false };
  }
}
